import moment from 'moment';
import type { BasketItemCardItem } from '../BasketItemCard';
import type {
  BasketItem,
  InvoicePreview,
  InvoiceLine,
} from '@portal/types/nexudus';
import {
  isTeamDiscountEligible,
  parseCreditFromDescription,
  getLineTeamDiscountPct,
  deriveTeamDiscountAmount,
  NO_TEAM_DISCOUNTS,
} from './getInvoiceDiscounts';
import type { TeamDiscounts } from './getInvoiceDiscounts';

export interface TransformBasketItemOptions {
  resourceImageBaseUrl: string;
  invoicePreview?: InvoicePreview | null;
  teamDiscounts?: TeamDiscounts;
  isLoadingInvoice?: boolean;
}

function formatAmount(amount: number): string {
  return `£${amount.toFixed(2)}`;
}

function getBookingDiscountLabels(
  line: InvoiceLine,
  teamDiscounts: TeamDiscounts,
): string[] {
  if (!isTeamDiscountEligible(line)) return [];

  const discount = line.DiscountAmount ?? 0;
  const linePct = getLineTeamDiscountPct(line, teamDiscounts);
  const labels: string[] = [];

  if (line.DiscountCode != null && discount > 0) {
    // A discount code is active; a team discount may or may not stack with it
    // (the team side is handled per-branch below via linePct).
    // Rows follow the order Nexudus applies them (BCD-1738
    // decision 1): the code first at its face value (DiscountAmount), then the
    // team discount derived from the post-code price — same attribution as the
    // summary breakdown in getInvoiceDiscounts, so the card never contradicts
    // it. A UnitPrice of 0 with a code is the 100%-code case: the code took
    // the whole base and there was nothing left for the team cut.
    if (linePct >= 100) {
      // A 100% team discount zeroes the line before the code can take
      // anything; DiscountAmount holds the full base price (staging test
      // TD100), so credit the team discount, not the code.
      labels.push(
        `${linePct}% Bruntwood Discount (-${formatAmount(discount)})`,
      );
      return labels;
    }
    labels.push(
      `Discount Code: ${line.DiscountCode} (-${formatAmount(discount)})`,
    );
    if (linePct > 0) {
      const teamAmount = deriveTeamDiscountAmount(line.UnitPrice, linePct);
      if (teamAmount > 0.005) {
        labels.push(
          `${linePct}% Bruntwood Discount (-${formatAmount(teamAmount)})`,
        );
      }
    }
    return labels;
  }

  if (discount > 0 && linePct > 0) {
    labels.push(`${linePct}% Bruntwood Discount (-${formatAmount(discount)})`);
  }

  return labels;
}

/**
 * Build a time-range string matching the Nexudus Description format.
 * e.g. "20/03/2026 13:00 > 14:00"
 */
function buildDescriptionTimeRange(fromTime: string, toTime: string): string {
  const from = moment(fromTime);
  const to = moment(toTime);
  return `${from.format('DD/MM/YYYY HH:mm')} > ${to.format('HH:mm')}`;
}

/**
 * Find the invoice line matching a booking basket item.
 * Matches by resource name + time range in the Description
 * (handles multiple bookings for the same room).
 */
function findBookingLine(
  invoice: InvoicePreview,
  bookingData: { ResourceName: string; FromTime?: string; ToTime?: string },
): InvoiceLine | undefined {
  const lines = invoice.Lines;
  if (!Array.isArray(lines)) return undefined;

  if (bookingData.FromTime && bookingData.ToTime) {
    const timeRange = buildDescriptionTimeRange(
      bookingData.FromTime,
      bookingData.ToTime,
    );
    const match = lines.find(
      (l) =>
        isTeamDiscountEligible(l) &&
        l.Description?.includes(bookingData.ResourceName) &&
        l.Description?.includes(timeRange),
    );
    if (match) return match;
  }
  return undefined;
}

/**
 * Check if an invoice line is an event ticket line.
 * Event lines have `EventAttendeeProductName` set to the ticket product name.
 */
function isEventLine(line: InvoiceLine): boolean {
  return line.EventAttendeeProductName != null;
}

/**
 * Find all invoice lines matching an event basket item.
 * Matches by event name prefix in the Description.
 */
function findEventLines(
  invoice: InvoicePreview,
  eventName: string,
): InvoiceLine[] {
  const lines = invoice.Lines;
  if (!Array.isArray(lines)) return [];

  return lines.filter(
    (l) => isEventLine(l) && l.Description?.startsWith(eventName + ':'),
  );
}

/**
 * Build discount labels for event lines (promo only — events don't get team discount).
 */
function getEventDiscountLabels(lines: InvoiceLine[]): string[] {
  const promoByCode = new Map<string, number>();

  for (const line of lines) {
    const discount = line.DiscountAmount ?? 0;
    if (line.DiscountCode != null && discount > 0) {
      promoByCode.set(
        line.DiscountCode,
        (promoByCode.get(line.DiscountCode) ?? 0) + discount,
      );
    }
  }

  const labels: string[] = [];
  for (const [code, amount] of promoByCode) {
    labels.push(`Discount Code: ${code} (-${formatAmount(amount)})`);
  }
  return labels;
}

/**
 * Transform a store basket item into the BasketItemCard display format.
 */
export function transformBasketItem(
  item: BasketItem,
  index: number,
  options: TransformBasketItemOptions,
): BasketItemCardItem {
  const isLoadingPrice = options.isLoadingInvoice ?? false;

  switch (item.type) {
    case 'booking': {
      const fromTime = moment(item.data.FromTime);
      const toTime = moment(item.data.ToTime);
      const image = `${options.resourceImageBaseUrl}/publicresources/getimage/${item.data.ResourceId}?h=128&w=128`;

      let effectiveBookingPrice = item.data.BookingPrice ?? 0;

      let discountLabels: string[] | undefined;
      let creditLabel: string | undefined;
      const teamDiscounts = options.teamDiscounts ?? NO_TEAM_DISCOUNTS;
      if (options.invoicePreview) {
        const line = findBookingLine(options.invoicePreview, item.data);
        if (line) {
          // Always use invoice line SubTotal — it reflects promos and credits
          // that BookingPrice (set at add-to-basket time) doesn't know about
          effectiveBookingPrice = line.SubTotal ?? 0;

          const creditAmount = parseCreditFromDescription(line.Description);
          if (creditAmount != null && creditAmount > 0) {
            discountLabels = undefined;
            creditLabel = `Booking credit applied (-${formatAmount(creditAmount)})`;
          } else {
            const labels = getBookingDiscountLabels(line, teamDiscounts);
            if (labels.length > 0) {
              discountLabels = labels;
            }
          }
        }
      }

      const selectedProducts = (item.data.ResourceProducts ?? []).filter(
        (p) => p.Quantity > 0,
      );
      const details = selectedProducts.map((p) => {
        const name = p.ProductName ?? 'Product';
        const price =
          p.Price != null ? ` (${formatAmount(p.Price * p.Quantity)})` : '';
        const label =
          p.Quantity > 1 ? `${p.Quantity}x ${name}${price}` : `${name}${price}`;
        return `+ ${label}`;
      });

      return {
        id: `${index}-${item.data.ResourceId}`,
        type: 'booking',
        name: item.data.ResourceName,
        subtitle: `${fromTime.format('ddd D MMM YYYY')} · ${fromTime.format('HH:mm')} - ${toTime.format('HH:mm')}`,
        price:
          effectiveBookingPrice +
          selectedProducts.reduce(
            (sum, p) => sum + (p.Price ?? 0) * p.Quantity,
            0,
          ),
        discountLabels,
        creditLabel,
        isLoadingPrice,
        image,
        details: details.length > 0 ? details : undefined,
      };
    }
    case 'event': {
      const selectedProducts = item.data.EventProducts.filter(
        (p) => p.Quantity > 0,
      );
      const basketTotal = selectedProducts.reduce(
        (sum, p) => sum + p.Quantity * p.Price,
        0,
      );
      const image = `${options.resourceImageBaseUrl}/Events/GetSmallImage?id=${item.data.Id}&w=96&h=96`;

      let effectivePrice = basketTotal;
      let discountLabels: string[] | undefined;

      if (options.invoicePreview) {
        const eventLines = findEventLines(
          options.invoicePreview,
          item.data.Name,
        );
        if (eventLines.length > 0) {
          effectivePrice = eventLines.reduce(
            (sum, l) => sum + (l.SubTotal ?? 0),
            0,
          );
          const labels = getEventDiscountLabels(eventLines);
          if (labels.length > 0) {
            discountLabels = labels;
          }
        }
      }

      return {
        id: `${index}-${item.data.Id}`,
        type: 'event',
        name: item.data.Name,
        subtitle: item.data.StartDate
          ? moment(item.data.StartDate).format('ddd D MMM, HH:mm')
          : undefined,
        price: effectivePrice,
        discountLabels,
        isLoadingPrice: options.isLoadingInvoice ?? false,
        image,
        details: selectedProducts.map((p) => `${p.Quantity}x ${p.Name}`),
      };
    }
    case 'contract':
      return {
        id: `${index}-${item.data.tariff?.Id ?? item.data.Id}`,
        type: 'contract',
        name: item.data.tariff?.Name || 'Plan',
        subtitle: item.data.startDate
          ? `Starts ${moment(item.data.startDate).format('D MMM YYYY')}`
          : undefined,
        price: item.data.BookingPrice ?? 0,
      };
    case 'product': {
      const image = `${options.resourceImageBaseUrl}/products/getImage?id=${item.data.Id}&w=96&h=96`;
      return {
        id: `${index}-${item.data.Id}`,
        type: 'product',
        name: `${item.data.Quantity ?? 1}x ${item.data.Name}`,
        price: (item.data.Price ?? 0) * (item.data.Quantity ?? 1),
        image,
      };
    }
    case 'course':
      return {
        id: `${index}-${item.data.Id}`,
        type: 'course',
        name: item.data.Title || 'Course',
        price: item.data.TariffPrice ?? 0,
        editable: false,
      };
  }
}
